import {Component, ElementRef, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {
    EdocEvent,
    EdocEventType,
    EdocMessage,
    EdocOptions,
    ForceReadingType,
    PesAccountService,
    PesDocument,
    PesStyle,
    PesTask,
    Transaction
} from '@pes/ui';
import {DocumentReadStateModel} from './Document-read-state.model';

/**
 * Displays a list of documents into an edoc transaction panel.
 */
@Component({
    selector: 'edoc-transaction-list',
    templateUrl: './edoc-transaction-list.component.html'
})
export class EdocTransactionListComponent {

    /**
     * The list of transaction for which to display documents.
     */
    @Input() public transactions: Transaction[] = null;

    /**
     * The reference to the current task.
     */
    @Input() public task: PesTask = null;

    /**
     * The list of attachments for the current task.
     */
    @Input() public attachments: Map<string, PesDocument[]> = null;

    /**
     * The PES style injected in the view to render the card.
     */
    @Input() public style: PesStyle = null;

    /**
     * The list of options for edoc.
     */
    @Input() public optionList: EdocOptions[] = null;

    /**
     * Event emitter invoked after each eDoc event.
     */
    @Output() public onEdocEvent: EventEmitter<EdocEvent> = new EventEmitter<EdocEvent>();
    /**
     * The ElementRef of the edoc component.
     */
    @ViewChild('edocElement', {read: ElementRef}) protected edocElement: ElementRef;
    /**
     * A map that contains references for all transactions Edoc options.
     */
    private readonly OPTIONS_MAP: Map<string, EdocOptions> = null;

    /**
     * A map to store the number of read documents by transactions.
     */
    private readonly NUM_DOC_MAP: Map<string, DocumentReadStateModel[]> = null;

    /**
     * Creates a new EdocTransactionListComponent instance.
     */
    constructor(protected account: PesAccountService) {
        this.OPTIONS_MAP = new Map<string, EdocOptions>();
        this.NUM_DOC_MAP = new Map<string, DocumentReadStateModel[]>();
    }

    /**
     * Returns the Edoc options associated with the specified transaction.
     *
     * @param {Transaction} transaction the transaction for which to get the options.
     * @returns {EdocOptions} the Edoc options associated with the specified transaction.
     */
    protected getEdocOptions(transaction: Transaction): EdocOptions {
        const id: string = transaction.id;
        if (this.optionList) {
            if (!this.OPTIONS_MAP.has(id)) {
                const options: EdocOptions = this.optionList.find((opt: EdocOptions) => {
                    return opt.transaction === transaction.id;
                });
                this.OPTIONS_MAP.set(id, options);
                if (options) {
                    const docsToRead: PesDocument[] = transaction.documents.filter((doc: PesDocument) => !doc.attachment && !doc.volatile);
                    this.NUM_DOC_MAP.set(
                        id,
                        docsToRead.map((doc, index) => {
                            return {docIndex: index, read: false} as DocumentReadStateModel
                        })
                    );
                }
            }
        } else {
            this.OPTIONS_MAP.set(id, null);
        }
        return this.OPTIONS_MAP.get(id);
    }

    /**
     * enableHandler based on different strategies
     * @param transaction
     * @protected
     */
    protected enableContraliaHandler(transaction: Transaction): boolean {
        return this.readingForced(transaction) || this.signWithEdoc(transaction);
    }

    /**
     *
     * @param transaction
     * @protected
     */
    protected signWithEdoc(transaction: Transaction): boolean {
        let result: boolean = false;
        const options: EdocOptions = this.getEdocOptions(transaction);
        if (options) {
            result = options.signWithEdoc;
        }

        return result;
    }

    /**
     * Returns a boolean that indicates whether the documents in the specified transaction must be entirely read.
     *
     * @param {Transaction} transaction the transaction for which to get the force reading option.
     *
     * @returns {boolean} true whether the documents in the specified transaction must be entirely read; false
     *                    otherwise.
     */
    protected readingForced(transaction: Transaction): boolean {
        let result: boolean = false;
        const options: EdocOptions = this.getEdocOptions(transaction);
        if (options) {
            result = options.forceReading === ForceReadingType.FULL_READING;
        }
        return result;
    }

    /**
     * The event handler that receives edoc events when enableContraliaEvent is true.
     *
     * @param {EdocMessage} message a message event fired by the edoc component.
     * @param {string} transactionId the id of the transaction associated with the specified message.
     */
    protected contraliaEventHandler(message: EdocMessage, transactionId: string): void {
        const evt: EdocEvent = this.dispatchEdocEvent(transactionId, message.name, message.params);
        if (evt.type === EdocEventType.EDOC_DOCREAD) {
            const documentReadStateList: DocumentReadStateModel[] = this.NUM_DOC_MAP.get(transactionId);
            if (documentReadStateList.length > message.params.docIndex) {
                documentReadStateList.find(doc => doc.docIndex === message.params.docIndex).read = true;
                this.NUM_DOC_MAP.set(transactionId, documentReadStateList);
                if (this.NUM_DOC_MAP.get(transactionId).filter(value => !value.read).length === 0) {
                    this.dispatchEdocEvent(transactionId, EdocEventType.EDOC_DOCREAD_ALL, null);
                }
            }
        }
    }

    /**
     * Returns a boolean that indicates whether the information message is displayed (true), or not (false).
     *
     * @param {Transaction} transaction the transaction for which to check the information message.
     *
     * @returns {boolean} true whether the information message is displayed; false otherwise.
     */
    protected showInfoMsg(transaction: Transaction): boolean {
        return this.NUM_DOC_MAP.get(transaction.id).filter(value => !value.read).length > 0;
    }

    /**
     * Returns a boolean that indicates whether the success message is displayed (true), or not (false).
     *
     * @param {Transaction} transaction the transaction for which to check the information success.
     *
     * @returns {boolean} true whether the success message is displayed; false otherwise.
     */
    protected showSuccessMsg(transaction: Transaction): boolean {
        return this.NUM_DOC_MAP.get(transaction.id).filter(value => !value.read).length === 0;
    }

    /**
     * Returns a boolean that indicates whether the specified transaction has attachments (true), or not (false).
     *
     * @param {Transaction} transaction the transaction to check.
     *
     * @returnq {boolean} true whether the specified transaction has attachments; false otherwise.
     */
    protected hasAttachments(transaction: Transaction): boolean {
        let result: boolean = false;
        if (this.attachments) {
            const id: string = transaction.id;
            result = this.attachments.has(id);
            if (result) {
                let docList: PesDocument[] = this.attachments.get(id);
                if (docList.length > 0) {
                    docList = docList.filter((doc: PesDocument) => {
                        return !doc.volatile;
                    });
                    result = (docList.length > 0);
                } else {
                    result = false;
                }
            }
        }
        return result;
    }

    /**
     * Dispatched a new EdocEvent instance built from the specified parameters.
     *
     * @param {string} transactionId the id of the transaction for the EdocEvent object to dispatch.
     * @param {EdocEventType} type the type of the EdocEvent object to dispatch.
     * @param {any} data the data associated with the EdocEvent object to dispatch.
     *
     * @returns {EdocEvent} the reference to the dispateched EdocEvent instance.
     */
    private dispatchEdocEvent(transactionId: string, type: EdocEventType, data: any): EdocEvent {
        const evt: EdocEvent = new EdocEvent(transactionId, type, data);
        this.onEdocEvent.emit(evt);
        return evt;
    }
}



// WEBPACK FOOTER //
// ./src/app/component/edoc-transaction-list/edoc-transaction-list.component.ts